// Text for the WebGL backend.
//
// Glyphs are rasterised once at a fixed size and stored in the atlas as signed
// distance fields, so a label stays crisp at any camera scale — which the map
// needs, because `autoSize` varies the scale continuously and text is drawn at
// everything from 8pt to 100px. Glyphs a distance field cannot represent (colour
// emoji) are kept as bitmaps instead; which of the two applies is decided by
// looking at what the browser actually rasterised rather than by guessing from
// the code point.
//
// Positioning comes from the same Canvas2D text engine the SVG renderer's layout
// ultimately shares, measured on string prefixes so that kerning is preserved.

import { MODE_GLYPH, MODE_IMAGE } from "./modes";

// Size the glyph is rasterised at, and the width of the distance field around its
// outline, both in raster pixels.
const RASTER_EM = 64;
const SDF_SPREAD = 8;
const COLOUR_PADDING = 1;

const LAYOUT_CACHE_LIMIT = 4096;

let scratch = null;
let measureCtx = null;

function getScratch() {
  if (!scratch) {
    const canvas = document.createElement("canvas");
    scratch = {
      canvas,
      ctx: canvas.getContext("2d", { willReadFrequently: true }),
    };
  }
  return scratch;
}

function getMeasureCtx() {
  if (!measureCtx) {
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = 1;
    measureCtx = canvas.getContext("2d");
  }
  return measureCtx;
}

// A font key is the CSS font shorthand at the raster size, so every size of the
// same face shares one set of glyphs.
export function fontKeyFor(style) {
  return (
    (style.fontStyle && style.fontStyle !== "normal"
      ? style.fontStyle + " "
      : "") +
    (style.fontWeight && style.fontWeight !== "normal"
      ? style.fontWeight + " "
      : "") +
    RASTER_EM +
    "px " +
    style.fontFamily
  );
}

// ---------------------------------------------------------------------------
// Font metrics
// ---------------------------------------------------------------------------

const metricsCache = new Map();

// All values are fractions of the em size.
export function fontMetrics(fontKey) {
  let m = metricsCache.get(fontKey);
  if (m) return m;

  const ctx = getMeasureCtx();
  ctx.font = fontKey;

  const em = ctx.measureText("M");
  const x = ctx.measureText("x");

  const ascent =
    (em.fontBoundingBoxAscent ||
      em.actualBoundingBoxAscent ||
      RASTER_EM * 0.8) / RASTER_EM;
  const descent =
    (em.fontBoundingBoxDescent ||
      em.actualBoundingBoxDescent ||
      RASTER_EM * 0.2) / RASTER_EM;
  const xHeight = (x.actualBoundingBoxAscent || RASTER_EM * 0.5) / RASTER_EM;

  m = { ascent, descent, xHeight };
  metricsCache.set(fontKey, m);
  return m;
}

// The offset from the requested y to the alphabetic baseline, in em units.
//
// SVG's `middle` puts the midpoint of the x-height on the alignment point and
// `hanging` uses the hanging baseline, which Chrome places at 80% of the
// ascender. Both are reproduced here rather than substituting Canvas2D's
// `textBaseline`, whose `middle` uses the em box and sits a little differently.
export function baselineOffset(dominantBaseline, metrics) {
  switch (dominantBaseline) {
    case "middle":
    case "central":
      return metrics.xHeight / 2;
    case "hanging":
    case "text-before-edge":
      return metrics.ascent * 0.8;
    case "text-after-edge":
    case "ideographic":
      return -metrics.descent;
    case "alphabetic":
    case "auto":
    case "baseline":
    default:
      return 0;
  }
}

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

let segmenter = null;
if (typeof Intl !== "undefined" && Intl.Segmenter) {
  try {
    segmenter = new Intl.Segmenter(undefined, { granularity: "grapheme" });
  } catch (e) {
    segmenter = null;
  }
}

function splitGraphemes(text) {
  if (segmenter) {
    const out = [];
    for (const part of segmenter.segment(text)) out.push(part.segment);
    return out;
  }
  return Array.from(text);
}

const layoutCache = new Map();

// Positions and the total advance, in em units, so one entry serves every size
// the same string is drawn at.
export function layoutText(text, fontKey) {
  const key = fontKey + "\u0000" + text;
  const cached = layoutCache.get(key);
  if (cached) return cached;

  const ctx = getMeasureCtx();
  ctx.font = fontKey;

  const clusters = splitGraphemes(text);
  const positions = new Array(clusters.length);

  // Measured on prefixes so that kerning between neighbours is included, which
  // summing per-glyph advances would lose.
  let prefix = "";
  for (let i = 0; i < clusters.length; i++) {
    positions[i] = ctx.measureText(prefix).width / RASTER_EM;
    prefix += clusters[i];
  }

  const result = {
    clusters,
    positions,
    width: ctx.measureText(text).width / RASTER_EM,
  };

  if (layoutCache.size >= LAYOUT_CACHE_LIMIT) layoutCache.clear();
  layoutCache.set(key, result);
  return result;
}

// ---------------------------------------------------------------------------
// Glyph rasterisation
// ---------------------------------------------------------------------------

const glyphCache = new Map();

// Returns a glyph record, or null when the cluster has no ink (a space) or the
// atlas is full. Coordinates in the record are em units relative to the pen
// position on the baseline, with y increasing downwards.
export function getGlyph(cluster, fontKey, atlas) {
  const key = fontKey + "\u0000" + cluster;
  const cached = glyphCache.get(key);
  if (cached !== undefined) return cached;

  const glyph = rasteriseGlyph(cluster, fontKey, atlas);
  glyphCache.set(key, glyph);
  return glyph;
}

function rasteriseGlyph(cluster, fontKey, atlas) {
  const { canvas, ctx } = getScratch();

  ctx.font = fontKey;
  const m = ctx.measureText(cluster);

  // Chrome reports the left edge as a positive distance to the left of the
  // origin; fall back to the advance box for fonts that do not supply it.
  const left =
    m.actualBoundingBoxLeft !== undefined ? m.actualBoundingBoxLeft : 0;
  const right =
    m.actualBoundingBoxRight !== undefined ? m.actualBoundingBoxRight : m.width;
  const ascent = m.actualBoundingBoxAscent || 0;
  const descent = m.actualBoundingBoxDescent || 0;

  const inkWidth = left + right;
  const inkHeight = ascent + descent;
  if (inkWidth <= 0 || inkHeight <= 0) return null;

  // Rasterise with generous padding, then decide whether the result can become a
  // distance field.
  const pad = SDF_SPREAD;
  const width = Math.ceil(inkWidth) + pad * 2;
  const height = Math.ceil(inkHeight) + pad * 2;

  canvas.width = width;
  canvas.height = height;

  ctx.clearRect(0, 0, width, height);
  ctx.font = fontKey;
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  ctx.fillStyle = "#ffffff";
  ctx.fillText(cluster, left + pad, ascent + pad);

  const image = ctx.getImageData(0, 0, width, height);
  const coloured = hasColour(image.data);

  if (coloured) {
    // Re-rasterise tightly: colour glyphs are sampled directly, so the distance
    // field padding is wasted atlas space.
    return rasteriseColourGlyph(
      cluster,
      fontKey,
      atlas,
      left,
      right,
      ascent,
      descent
    );
  }

  toDistanceField(image.data, width, height, SDF_SPREAD);

  const slot = atlas.allocate(width, height);
  if (!slot) return null;
  atlas.upload(slot, image);

  return {
    mode: MODE_GLYPH,
    slot,
    x0: (-left - pad) / RASTER_EM,
    y0: (-ascent - pad) / RASTER_EM,
    x1: (right + pad) / RASTER_EM,
    y1: (descent + pad) / RASTER_EM,
  };
}

function rasteriseColourGlyph(
  cluster,
  fontKey,
  atlas,
  left,
  right,
  ascent,
  descent
) {
  const { canvas, ctx } = getScratch();

  const pad = COLOUR_PADDING;
  const width = Math.ceil(left + right) + pad * 2;
  const height = Math.ceil(ascent + descent) + pad * 2;

  canvas.width = width;
  canvas.height = height;

  ctx.clearRect(0, 0, width, height);
  ctx.font = fontKey;
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  ctx.fillStyle = "#000000";
  ctx.fillText(cluster, left + pad, ascent + pad);

  const slot = atlas.allocate(width, height);
  if (!slot) return null;
  atlas.upload(slot, ctx.getImageData(0, 0, width, height));

  return {
    mode: MODE_IMAGE,
    slot,
    x0: (-left - pad) / RASTER_EM,
    y0: (-ascent - pad) / RASTER_EM,
    x1: (right + pad) / RASTER_EM,
    y1: (descent + pad) / RASTER_EM,
  };
}

function hasColour(data) {
  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] === 0) continue;
    if (data[i] !== data[i + 1] || data[i + 1] !== data[i + 2]) return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// Distance field
// ---------------------------------------------------------------------------

// 8SSEDT, run once over the inside and once over the outside, rewriting the
// alpha channel in place: 0.5 lands on the outline, higher is inside.
function toDistanceField(data, width, height, spread) {
  const count = width * height;

  // Distance from each pixel to the nearest pixel of the opposite kind. A pixel
  // inside the glyph is zero away from "inside" and some way from "outside", so
  // the two together give the sign as well as the magnitude.
  const distToInside = new Float64Array(count);
  const distToOutside = new Float64Array(count);

  const gx = new Float64Array(count);
  const gy = new Float64Array(count);

  edt(data, width, height, true, distToInside, gx, gy);
  edt(data, width, height, false, distToOutside, gx, gy);

  for (let i = 0; i < count; i++) {
    // Positive inside, negative outside.
    const signed = Math.sqrt(distToOutside[i]) - Math.sqrt(distToInside[i]);
    let v = 0.5 + signed / (2 * spread);
    if (v < 0) v = 0;
    else if (v > 1) v = 1;

    const p = i * 4;
    data[p] = 255;
    data[p + 1] = 255;
    data[p + 2] = 255;
    data[p + 3] = Math.round(v * 255);
  }
}

const INF = 1e12;

// Squared euclidean distance transform of the set selected by `wantInside`.
// `dxBuf`/`dyBuf` are scratch vectors reused between the two passes.
function edt(data, width, height, wantInside, out, dxBuf, dyBuf) {
  const count = width * height;

  for (let i = 0; i < count; i++) {
    const alpha = data[i * 4 + 3];
    const isInside = alpha >= 128;
    // Seeds are the pixels of the set being measured *from*: distance is zero
    // there and grows outwards.
    if (isInside === wantInside) {
      out[i] = 0;
      dxBuf[i] = 0;
      dyBuf[i] = 0;
    } else {
      out[i] = INF;
      dxBuf[i] = INF;
      dyBuf[i] = INF;
    }
  }

  const compare = (x, y, ox, oy) => {
    const nx = x + ox;
    const ny = y + oy;
    if (nx < 0 || ny < 0 || nx >= width || ny >= height) return;

    const from = ny * width + nx;
    const to = y * width + x;

    const ddx = dxBuf[from] - ox;
    const ddy = dyBuf[from] - oy;
    const dist = ddx * ddx + ddy * ddy;
    if (dist < out[to]) {
      out[to] = dist;
      dxBuf[to] = ddx;
      dyBuf[to] = ddy;
    }
  };

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      compare(x, y, -1, 0);
      compare(x, y, 0, -1);
      compare(x, y, -1, -1);
      compare(x, y, 1, -1);
    }
    for (let x = width - 1; x >= 0; x--) compare(x, y, 1, 0);
  }

  for (let y = height - 1; y >= 0; y--) {
    for (let x = width - 1; x >= 0; x--) {
      compare(x, y, 1, 0);
      compare(x, y, 0, 1);
      compare(x, y, 1, 1);
      compare(x, y, -1, 1);
    }
    for (let x = 0; x < width; x++) compare(x, y, -1, 0);
  }
}

// Every glyph record holds an atlas slot, so losing the atlas — which happens
// when the GL context is lost and rebuilt — invalidates all of them.
export function resetGlyphCache() {
  glyphCache.clear();
}

export { RASTER_EM };
